import { Add, Delete } from "@mui/icons-material";
import {
  Box,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { getTranslation } from "common";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import { useTranslations } from "hooks";
import type {
  IsoWeekday,
  WorkingInterval,
} from "model/organizationCalendarModel";
import React, { useMemo } from "react";
import CalendarFieldLabel, {
  calendarDescriptionId,
} from "./CalendarFieldLabel";
import { validateIntervals } from "./calendarValidation";
import CalendarPickerField from "./CalendarPickerField";

const DAYS: IsoWeekday[] = [1, 2, 3, 4, 5, 6, 7];
const DEFAULT_DAY_INTERVALS = [
  { startsAt: "09:00", endsAt: "13:00" },
  { startsAt: "14:00", endsAt: "18:00" },
];

interface WorkingWeekEditorProps {
  intervals: WorkingInterval[];
  onChange: (next: WorkingInterval[]) => void;
  disabled?: boolean;
}

const WorkingWeekEditor: React.FC<WorkingWeekEditorProps> = ({
  intervals,
  onChange,
  disabled = false,
}) => {
  const translations = useTranslations();
  const weekdayNames = useMemo(
    () =>
      DAYS.map((day) =>
        new Intl.DateTimeFormat(navigator.language, {
          weekday: "long",
          timeZone: "UTC",
        }).format(new Date(Date.UTC(2024, 0, day)))
      ),
    []
  );

  const updateInterval = (
    day: IsoWeekday,
    index: number,
    patch: Partial<WorkingInterval>
  ) => {
    const target = intervals.filter(
      (interval) => interval.isoWeekday === day
    )[index];
    onChange(
      intervals.map((interval) =>
        interval === target ? { ...interval, ...patch } : interval
      )
    );
  };

  const setDayEnabled = (day: IsoWeekday, enabled: boolean) => {
    if (!enabled) {
      onChange(
        intervals.filter((interval) => interval.isoWeekday !== day)
      );
      return;
    }
    onChange([
      ...intervals,
      ...DEFAULT_DAY_INTERVALS.map((interval) => ({
        isoWeekday: day,
        ...interval,
      })),
    ]);
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" fontWeight={700}>
        {getTranslation(translations, "calendar.week.label")}
      </Typography>
      {DAYS.map((day, dayIndex) => {
        const dayIntervals = intervals.filter(
          (interval) => interval.isoWeekday === day
        );
        const errors = validateIntervals(
          dayIntervals,
          `weekday-${day}`
        );
        const enabled = dayIntervals.length > 0;
        const switchFieldId = `weekday-${day}-enabled`;

        return (
          <Box
            key={day}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  disabled={disabled}
                  onChange={(_, checked) => setDayEnabled(day, checked)}
                  inputProps={{
                    "aria-label": weekdayNames[dayIndex],
                    "aria-describedby":
                      calendarDescriptionId(switchFieldId),
                    "data-calendar-field": "true",
                    "data-calendar-field-id": switchFieldId,
                    "data-testid": `calendar-weekday-${day}-enabled`,
                  } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              }
              label={
                <CalendarFieldLabel
                  fieldId={switchFieldId}
                  label={weekdayNames[dayIndex]}
                  help={getTranslation(
                    translations,
                    "calendar.weekday.enabled.help"
                  )}
                />
              }
            />
            {dayIntervals.map((interval, index) => {
              const error = errors.find(
                (item) => item.key === `weekday-${day}-${index}`
              );
              const startFieldId = `weekday-${day}-interval-${index}-start`;
              const endFieldId = `weekday-${day}-interval-${index}-end`;

              return (
                <Stack
                  key={`${day}-${index}`}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ sm: "center" }}
                  sx={{ mt: 1 }}
                >
                  <Box sx={{ flex: 1 }}>
                    <CalendarFieldLabel
                      fieldId={startFieldId}
                      label={getTranslation(
                        translations,
                        "calendar.interval.start.label"
                      )}
                      help={getTranslation(
                        translations,
                        "calendar.interval.start.help"
                      )}
                    />
                    <CalendarPickerField
                      fullWidth
                      size="small"
                      type="time"
                      value={interval.startsAt}
                      disabled={disabled}
                      error={Boolean(error)}
                      onChange={(value) =>
                        updateInterval(day, index, {
                          startsAt: value,
                        })
                      }
                      inputProps={{
                        step: 60,
                        "aria-label": getTranslation(
                          translations,
                          "calendar.interval.start.label"
                        ),
                        "aria-describedby":
                          calendarDescriptionId(startFieldId),
                        "data-calendar-field": "true",
                        "data-calendar-field-id": startFieldId,
                        "data-testid": `calendar-weekday-${day}-interval-${index}-start`,
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <CalendarFieldLabel
                      fieldId={endFieldId}
                      label={getTranslation(
                        translations,
                        "calendar.interval.end.label"
                      )}
                      help={getTranslation(
                        translations,
                        "calendar.interval.end.help"
                      )}
                    />
                    <CalendarPickerField
                      fullWidth
                      size="small"
                      type="time"
                      value={interval.endsAt}
                      disabled={disabled}
                      error={Boolean(error)}
                      helperText={
                        error
                          ? getTranslation(translations, error.messageKey)
                          : undefined
                      }
                      onChange={(value) =>
                        updateInterval(day, index, {
                          endsAt: value,
                        })
                      }
                      inputProps={{
                        step: 60,
                        "aria-label": getTranslation(
                          translations,
                          "calendar.interval.end.label"
                        ),
                        "aria-describedby":
                          calendarDescriptionId(endFieldId),
                        "data-calendar-field": "true",
                        "data-calendar-field-id": endFieldId,
                        "data-testid": `calendar-weekday-${day}-interval-${index}-end`,
                      }}
                    />
                  </Box>
                  <IconButton
                    disabled={disabled}
                    data-testid={`calendar-weekday-${day}-interval-${index}-remove`}
                    aria-label={getTranslation(
                      translations,
                      "calendar.interval.remove"
                    )}
                    onClick={() =>
                      onChange(
                        intervals.filter((item) => item !== interval)
                      )
                    }
                  >
                    <Delete />
                  </IconButton>
                </Stack>
              );
            })}
            {enabled && (
              <FlowentiButton
                size="small"
                variant="outlined"
                startIcon={<Add />}
                disabled={disabled}
                data-testid={`calendar-weekday-${day}-interval-add`}
                sx={{ mt: 1 }}
                onClick={() =>
                  onChange([
                    ...intervals,
                    {
                      isoWeekday: day,
                      startsAt: "18:00",
                      endsAt: "19:00",
                    },
                  ])
                }
              >
                {getTranslation(translations, "calendar.interval.add")}
              </FlowentiButton>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

export default WorkingWeekEditor;
